import { gql } from '@apollo/client';
import { CHAT_PARTS_DEFAULT, CHAT_PARTS_MAX } from '@arthelper/shared/chat-parts';

export const USER_BADGE_FIELDS = gql`
  fragment UserBadgeFields on UserBadge {
    id
    contestWins {
      place
      contestName
      contestThumbnailUrl
    }
    badge {
      id
      name
      color
      icon
      hoverText
      url
      iconOnly
      thumbnailUrl
      ctaText
      ctaUrl
      ctaBgColor
      ctaTextColor
    }
  }
`;

// The actor shape for notification rows, which render the verified check and the badge
// chips beside a name. The `id` inside `profile` is the load-bearing part: with it Apollo
// normalizes the Profile as its own entity and merges writes field by field, so another
// document selecting fewer profile fields leaves the rest of the entity intact. Without
// it the Profile stays embedded under the `asfId`-keyed User and every write replaces it
// whole, so the narrowest writer wins and the other surface reads a hole.
export const ACTOR_FIELDS = gql`
  ${USER_BADGE_FIELDS}

  fragment ActorFields on User {
    asfId
    firstName
    lastName
    username
    profile {
      id
      slug
      avatarUrl
      displayName
      isHumanVerified
      badges {
        ...UserBadgeFields
      }
    }
  }
`;

// Community-post and comment authors: a name and an avatar are all these surfaces draw,
// so the selection stops there. `commentThreads(limit: 20)` asks for up to 21 authors per
// modal open, and `Profile.badges` is not loader-batched for an author who has valued
// critiques — pulling badges nothing renders buys a per-author query fan-out. The `id`
// keeps the narrow shape safe: the Profile still normalizes, so these writes merge into
// the same entity the actor rows read instead of replacing it.
export const POST_AUTHOR_FIELDS = gql`
  fragment PostAuthorFields on User {
    asfId
    firstName
    lastName
    profile {
      id
      slug
      displayName
      avatarUrl
    }
  }
`;

export const PROFILE_QUERY = gql`
  query Profile($asfId: ID!) {
    user(asfId: $asfId) {
      asfId
      email
      firstName
      lastName
      story
      username
      brandVoice
      voiceTones {
        id
        name
      }
      voiceFormalities {
        id
        name
      }
      voiceAvoidWords {
        id
        words
      }
      permissions {
        id
        key
      }
      firstUtmSource
      createdAt
      updatedAt
      isOldAsfMember
      isCoreFunnelLead
      hasSettingsInstructions
      settingsInstructions
      activityEmailEnabled
      copyrightEmailEnabled
      voteEmailEnabled
      trendingEmailEnabled
      followPostEmailEnabled
      isDailyTrendingDigestEmailEnabled
      isInactivityDigestEmailEnabled
      communityNotificationEmailEnabled
      connectionEmailEnabled
      dmEmailEnabled
      recommendedConnectionsEmailEnabled
      isWeeklyReachEnabled
      isDefaultArtworkPublic
      emailUnsubscribedAll
      artyTipsEmailEnabled
      companyAnnouncementEmailEnabled
      guildEmailEnabled
      bountyEmailEnabled
      onlyGuildNotifications
      profile {
        id
        userId
        avatarUrl
        bio
        displayName
        artistStatement
        countryCode
        country
        regionCode
        region
        city
        latitude
        longitude
        timezone
        bannerUrl
        slug
        socialLinks
        phoneNumber
        hasMockupDownloads
        lastPhoneCaptureAttempt
        isPublic
        isDiscoverable
        isDisabled
        isNsfw
        imageOrder
        watermarkEnabled
        watermarkUrl
        categories {
          id
          name
          slug
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const MY_PROFILE_QUERY = gql`
  query MyProfile {
    profile {
      id
      user {
        asfId
        email
        firstName
        lastName
        story
        username
        brandVoice
        voiceTones {
          id
          name
        }
        voiceFormalities {
          id
          name
        }
        voiceAvoidWords {
          id
          words
        }
        permissions {
          id
          key
        }
        firstUtmSource
        createdAt
        updatedAt
        isOldAsfMember
        isCoreFunnelLead
        hasSettingsInstructions
        settingsInstructions
        activityEmailEnabled
        copyrightEmailEnabled
        voteEmailEnabled
        trendingEmailEnabled
        followPostEmailEnabled
        isDailyTrendingDigestEmailEnabled
        isInactivityDigestEmailEnabled
        communityNotificationEmailEnabled
        connectionEmailEnabled
        dmEmailEnabled
        recommendedConnectionsEmailEnabled
        isWeeklyReachEnabled
        isDefaultArtworkPublic
        emailUnsubscribedAll
        artyTipsEmailEnabled
        companyAnnouncementEmailEnabled
        guildEmailEnabled
        bountyEmailEnabled
        onlyGuildNotifications
        profile {
          id
          userId
          avatarUrl
          bio
          displayName
          artistStatement
          countryCode
          country
          regionCode
          region
          city
          latitude
          longitude
          timezone
          locale
          autoTranslateEnabled
          autoTranslateExcludedLocales
          localeSource
          bannerUrl
          slug
          socialLinks
          phoneNumber
          hasMockupDownloads
          lastPhoneCaptureAttempt
          isPublic
          isDiscoverable
          isDisabled
          isNsfw
          imageOrder
          watermarkEnabled
          watermarkUrl
          showMockups
          showArtworkSpecs
          showActivity
          isAnnounceNewArtwork
          isHumanVerified
          categories {
            id
            name
            slug
          }
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const IMAGE_COUNT_QUERY = gql`
  query ImageCount {
    imageStats {
      count
    }
  }
`;

/** One page of the MediaSelector picker — slim tile fields plus what a pick hands back. */
export const MEDIA_SELECTOR_IMAGES_QUERY = gql`
  query MediaSelectorImages($first: Int, $after: String, $filter: ImageFilter) {
    myImages(first: $first, after: $after, filter: $filter) {
      edges {
        cursor
        node {
          id
          title
          description
          status
          fileName
          thumbnailUrl
          optimizedUrl
          originalWidth
          originalHeight
          width
          height
          dimensionType
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const IMAGE_QUERY = gql`
  query Image($id: ID!) {
    image(id: $id) {
      id
      title
      tags {
        id
        name
      }
      status
      description
      optimizedUrl
      thumbnailUrl
      width
      height
      dimensionType
      isPublic
      slug
      originalWidth
      originalHeight
      likes
      saves {
        id
        content
        completion {
          id
          type {
            name
            icon
          }
          image {
            id
            title
            thumbnailUrl
            optimizedUrl
            width
            height
            dimensionType
          }
          responseHtml
        }
        labels {
          id
          name
        }
        createdAt
      }
    }
  }
`;

export const GENERATIONS_QUERY = gql`
  query Generations($id: ID!) {
    generations(id: $id) {
      imageId
      uuid
      id
      width
      height
      dimensionType
      scaleFactor
    }
  }
`;

export const COMPLETION_TYPES_QUERY = gql`
  query CompletionTypes {
    completionTypes {
      id
      name
      description
      promptData
      permissionKey
      icon
      order
      userRules {
        id
        active
        rules
      }
      planNames
      expiresAt
      enabled
      createdAt
      favoriteCompletionTypes {
        id
      }
      usages {
        id
        usageCount
      }
      additionalInfo
      signupHeaderText
      signupFormLabelText
      signupButtonText
      linkOutUrl
      isLinkOutNewWindow
      isLinkOut
    }
  }
`;

export const COMPLETION_TYPE_SIGNUP_QUERY = gql`
  query CompletionTypeSignup($id: ID!) {
    sparkMarketing(id: $id) {
      id
      name
      signupHeaderText
      signupFormLabelText
      signupButtonText
    }
  }
`;

export const COMPLETIONS_QUERY = gql`
  query Completions($type: ID!, $targetType: String!, $targetId: ID!) {
    completions(type: $type, targetType: $targetType, targetId: $targetId) {
      id
      image {
        id
        title
        thumbnailUrl
        optimizedUrl
        width
        height
        dimensionType
        slug
      }
      response
      suggestion {
        content
      }
      targetType
      targetId
      series {
        id
        name
        images {
          id
          title
          thumbnailUrl
          optimizedUrl
          width
          height
          dimensionType
        }
      }
      collection {
        id
        name
        imageCount(nested: true)
        nestedImages(limit: 10) {
          id
          title
          thumbnailUrl
          optimizedUrl
          width
          height
          dimensionType
        }
      }
      responseHtml
      createdAt
    }
  }
`;

export const UPSCALE_QUERY = gql`
  query upscaleStatus($id: ID!) {
    image(id: $id) {
      id
      upscaledUrl
      likes
    }
  }
`;

export const USER_SAVES_QUERY = gql`
  query GetUserSaves {
    userSaves {
      id
      content
      meta
      completion {
        id
        type {
          id
          name
          icon
        }
        targetType
        targetId
        image {
          id
          title
          thumbnailUrl
          optimizedUrl
          width
          height
          dimensionType
        }
        collection {
          id
          name
          imageCount(nested: true)
          images(limit: 10) {
            id
            title
            thumbnailUrl
            optimizedUrl
            width
            height
            dimensionType
          }
          nestedImages(limit: 10) {
            id
            title
            thumbnailUrl
            optimizedUrl
            width
            height
            dimensionType
          }
        }
        series {
          id
          name
          images {
            id
            title
            thumbnailUrl
            optimizedUrl
            width
            height
            dimensionType
          }
        }
        responseHtml
      }
      labels {
        id
        name
      }
      createdAt
    }
  }
`;

export const USER_MOCK_SAVES_QUERY = gql`
  query GetUserMockSaves($imageId: ID) {
    userMockSaves(imageId: $imageId) {
      id
      imageId
      mockId
      image {
        id
        title
        thumbnailUrl
        optimizedUrl
        width
        height
        dimensionType
      }
      mock {
        id
        uuid
        description
        tags
        palette
        scaleFactor
        enabled
      }
      createdAt
    }
  }
`;

export const IMAGE_HAS_SAVED_MOCK = gql`
  query ImageHasSavedMock($imageId: ID!, $mockId: ID!) {
    imageHasSavedMock(imageId: $imageId, mockId: $mockId)
  }
`;

export const LABELS_QUERY = gql`
  query Labels {
    labels {
      id
      name
    }
  }
`;

export const SUBSCRIPTION_QUERY = gql`
  query Subscriptions($asfId: ID!) {
    user(asfId: $asfId) {
      asfId
      subscriptions
      humanMadeArtSubscriptionStatus
    }
    prices {
      id
      unit_amount
      product {
        id
        name
        marketing_features {
          name
        }
      }
    }
  }
`;

export const CHECKOUT_SESSION_QUERY = gql`
  query CreateCheckoutSession($priceId: String!, $fptid: String, $productLine: String) {
    checkoutSession(priceId: $priceId, fptid: $fptid, productLine: $productLine) {
      id
      url
    }
  }
`;

export const CHECKOUT_PURCHASE_QUERY = gql`
  query CheckoutPurchase($sessionId: String!) {
    checkoutPurchase(sessionId: $sessionId) {
      value
      currency
    }
  }
`;

export const TOKENS_QUERY = gql`
  query Tokens {
    tokens
  }
`;

export const LIMITS_QUERY = gql`
  query Limits {
    prices {
      id
      product {
        id
        metadata
      }
    }
  }
`;

export const INTEGRATIONS_QUERY = gql`
  query Integrations {
    integrations {
      id
      name
      type
      config
      enabled
      icon
      description
      userIntegrations(status: "active") {
        id
        integration {
          id
        }
        accountId
        status
        metadata
        credentials
        createdAt
        updatedAt
      }
    }
  }
`;

export const FETCH_MOCKS = gql`
  query FetchMocks($uuids: [String!], $filterIds: [ID!]) {
    mocks(uuids: $uuids, filterIds: $filterIds) {
      id
      uuid
      tags
      description
      palette
      scaleFactor
      maxWidth
      maxHeight
      enabled
      filters {
        id
        name
      }
      createdAt
      updatedAt
    }
  }
`;

export const FETCH_MOCK_FILTERS = gql`
  query FetchMockFilters {
    mockFilters {
      id
      name
    }
  }
`;

export const ADMIN_MOCKS = gql`
  query AdminMocks {
    adminMocks {
      id
      uuid
      tags
      description
      palette
      scaleFactor
      maxWidth
      maxHeight
      enabled
      filters {
        id
        name
      }
      createdAt
      updatedAt
      deletedAt
    }
  }
`;

export const GET_SCHEDULED_POSTS = gql`
  query GetScheduledPosts($from: DateTime, $to: DateTime, $status: PostStatus) {
    socialPosts(from: $from, to: $to, status: $status) {
      id
      createdAt
      mediaUrls
      content
      scheduledFor
      status
      error
      platformPostId
      platformPostUrl
      inputProps
      isReel
      userIntegration {
        id
        accountId
        status
        metadata
        integration {
          id
          name
          icon
        }
      }
    }
  }
`;

export const GET_SOCIAL_POST = gql`
  query GetSocialPost($id: ID!) {
    socialPost(id: $id) {
      id
      createdAt
      mediaUrls
      content
      scheduledFor
      status
      error
      platformPostId
      platformPostUrl
      userIntegration {
        id
        accountId
        status
        metadata
        integration {
          id
          name
          icon
        }
      }
    }
  }
`;

export const ONBOARDING_QUERY = gql`
  query Onboarding {
    onboarding {
      id
      userId
      website {
        name
        subdomain
        activeThemeId
        resellerId
      }
      parentWebsite {
        fulfillmentPartnerId
        defaultParentStoreId
      }
      override {
        contact {
          firstName
          lastName
          email
          address {
            street
            street2
            city
            state
            zip
            country
          }
        }
        website {
          address {
            street
            street2
            city
            state
            zip
            country
          }
          logoImage
          description
        }
      }
      planId
      subdomainOfSetupWebsite
      type
      delayDependenciesCreation
      about
      createdAt
      updatedAt
    }
  }
`;

/**
 * One my-artwork row. Slim on purpose: `fileName`/`fileSize` instead of the `fileMeta`,
 * `aiMeta`, `printMeta` and `artworkSpecs` blobs, which only the details sheet needs
 * (`ARTWORK_EDIT_IMAGE_QUERY`).
 */
const LIBRARY_IMAGE_FIELDS = gql`
  fragment LibraryImageFields on Image {
    id
    title
    description
    status
    context
    thumbnailUrl
    optimizedUrl
    originalUrl
    originalWidth
    originalHeight
    fileName
    fileSize
    collectionId
    height
    width
    dimensionType
    isPublic
    isNsfw
    flagReason
    slug
    tags {
      id
      name
    }
    createdAt
  }
`;

/** A page of the my-artwork grid; also the processing poll (`filter.ids`) and its seed. */
export const LIBRARY_IMAGES_QUERY = gql`
  ${LIBRARY_IMAGE_FIELDS}
  query LibraryImages($first: Int, $after: String, $filter: ImageFilter, $sort: ImageSort) {
    myImages(first: $first, after: $after, filter: $filter, sort: $sort) {
      edges {
        cursor
        node {
          ...LibraryImageFields
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
      asOf
    }
  }
`;

/** Folders without their image lists: six preview tiles and a server count each. */
export const LIBRARY_COLLECTIONS_QUERY = gql`
  query LibraryCollections {
    collections {
      id
      name
      path
      description
      imageCount(nested: true)
      nestedImages(limit: 6) {
        id
        title
        thumbnailUrl
        optimizedUrl
        width
        height
        dimensionType
        isPublic
      }
      ephemeral
      createdAt
      updatedAt
    }
  }
`;

/** The my-artwork header pill: every non-hidden image in any status, and its storage. */
export const LIBRARY_STATS_QUERY = gql`
  query LibraryStats {
    imageStats {
      count
      totalBytes
    }
  }
`;

/**
 * VisibilityFilterTabs counts over the active folder scope, plus the processed library
 * total for the tree's root row, in one request.
 */
export const LIBRARY_VISIBILITY_STATS_QUERY = gql`
  query LibraryVisibilityStats(
    $all: ImageFilter
    $onProfile: ImageFilter
    $private: ImageFilter
    $library: ImageFilter
  ) {
    all: imageStats(filter: $all) {
      count
    }
    onProfile: imageStats(filter: $onProfile) {
      count
    }
    private: imageStats(filter: $private) {
      count
    }
    library: imageStats(filter: $library) {
      count
    }
  }
`;

/** The full row the my-artwork details sheet edits; grid rows are too slim for it. */
export const ARTWORK_EDIT_IMAGE_QUERY = gql`
  query ArtworkEditImage($id: ID!) {
    image(id: $id) {
      id
      title
      description
      status
      context
      thumbnailUrl
      optimizedUrl
      originalUrl
      originalWidth
      originalHeight
      fileMeta
      collectionId
      height
      width
      dimensionType
      isPublic
      isNsfw
      flagReason
      slug
      artworkSpecs {
        mediumType
        camera
        lens
        focalLength
        aperture
        shutterSpeed
        iso
        medium
        technique
        subMedium
        software
        mediumCategory {
          id
        }
        hidden
      }
      tags {
        id
        name
      }
      createdAt
    }
  }
`;

export const GET_IMAGE_SOCIAL_POSTS = gql`
  query GetImageSocialPosts($imageId: ID!, $limit: Int!, $offset: Int!) {
    socialPostsForImage(imageId: $imageId, limit: $limit, offset: $offset) {
      posts {
        id
        createdAt
        mediaUrls
        content
        scheduledFor
        postedAt
        status
        error
        platformPostId
        platformPostUrl
        userIntegration {
          id
          accountId
          integration {
            id
            name
            icon
          }
        }
      }
      total
    }
  }
`;

export const COLLECTION_QUERY = gql`
  query Collection($id: ID!) {
    collection(id: $id) {
      id
      name
      path
      description
      images(limit: 1) {
        id
        title
        thumbnailUrl
        optimizedUrl
        width
        height
        dimensionType
        isPublic
      }
      nestedImages(limit: 50) {
        id
        title
        thumbnailUrl
        optimizedUrl
        width
        height
        dimensionType
        isPublic
      }
      saves {
        id
        content
        completion {
          id
          type {
            name
            icon
          }
          collection {
            id
            name
            path
            imageCount(nested: true)
            images(limit: 10) {
              id
              title
              thumbnailUrl
              optimizedUrl
              width
              height
              dimensionType
            }
            nestedImages(limit: 10) {
              id
              title
              thumbnailUrl
              optimizedUrl
              width
              height
              dimensionType
            }
          }
          responseHtml
        }
        labels {
          id
          name
        }
        createdAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const SERIES_QUERY = gql`
  query Series($id: ID!) {
    series(id: $id) {
      id
      name
      description
      images {
        id
        title
        thumbnailUrl
        optimizedUrl
        originalUrl
        createdAt
        width
        height
        dimensionType
      }
      saves {
        id
        content
        meta
        completion {
          id
          type {
            id
            name
            icon
          }
          targetType
          targetId
          image {
            id
            title
            thumbnailUrl
            optimizedUrl
            width
            height
            dimensionType
          }
          collection {
            id
            name
            imageCount(nested: true)
            images(limit: 10) {
              id
              title
              thumbnailUrl
              optimizedUrl
              width
              height
              dimensionType
            }
          }
          series {
            id
            name
            images {
              id
              title
              thumbnailUrl
              optimizedUrl
              width
              height
              dimensionType
            }
          }
          responseHtml
        }
        labels {
          id
          name
        }
        createdAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const HAS_COMPLETIONS_QUERY = gql`
  query HasCompletions {
    hasCompletions
  }
`;

export const ARTWORK_AVATAR_QUERY = gql`
  query ArtworkAvatar {
    artworkAvatar
  }
`;

// Keyset-paged chat list for the sidebar, flyout history and conversation picker - slim rows, no parts
export const MY_CHATS_QUERY = gql`
  query MyChats($first: Int, $after: String, $type: ChatType, $targetId: ID) {
    myChats(first: $first, after: $after, type: $type, targetId: $targetId) {
      edges {
        cursor
        node {
          id
          title
          type
          targetId
          targetType
          shareParams
          shared
          createdAt
          updatedAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const CHAT_PART_FIELDS = `
  id
  role
  partIndex
  type
  content
  toolCall
  attachment
  reasoning
  usage
  good
  metadata
  createdAt
`;

// One opened chat: the newest `$last` parts with partIndex < `$before` (omitted = newest).
// `partCount` tells a truncated window from a complete one.
export const CHAT_QUERY = gql`
  query Chat($id: ID!, $before: Int, $last: Int = ${CHAT_PARTS_DEFAULT}) {
    chat(id: $id) {
      id
      title
      type
      targetId
      targetType
      shareParams
      shared
      partCount
      parts(last: $last, before: $before) {
        ${CHAT_PART_FIELDS}
      }
      createdAt
      updatedAt
    }
  }
`;

// A chat started in this session: its header and share state, never its parts.
export const CHAT_META_QUERY = gql`
  query ChatMeta($id: ID!) {
    chat(id: $id) {
      id
      title
      type
      targetId
      targetType
      shareParams
      shared
      createdAt
      updatedAt
    }
  }
`;

// Verifies a remembered per-artwork conversation still belongs to that artwork.
export const CHAT_TARGET_QUERY = gql`
  query ChatTarget($id: ID!) {
    chat(id: $id) {
      id
      type
      targetId
      targetType
    }
  }
`;

// Read-only transcript of a chat whose artwork was deleted: one bounded window, no paging.
export const DELETED_CONTENT_CHAT_QUERY = gql`
  query DeletedContentChat($id: ID!, $last: Int = ${CHAT_PARTS_MAX}) {
    chat(id: $id) {
      id
      title
      type
      targetId
      targetType
      partCount
      parts(last: $last) {
        ${CHAT_PART_FIELDS}
      }
      createdAt
      updatedAt
    }
  }
`;

export const CHAT_TITLE_QUERY = gql`
  query ChatTitle($id: ID!) {
    chat(id: $id) {
      id
      title
    }
  }
`;

// Shared / eval views: one bounded window (the server max), no paging.
export const SHARED_CHAT_QUERY = gql`
  query SharedChat($id: ID!, $last: Int = ${CHAT_PARTS_MAX}) {
    share(id: $id) {
      id
      title
      type
      targetId
      targetType
      partCount
      parts(last: $last) {
        id
        role
        partIndex
        type
        content
        toolCall
        attachment
        reasoning
        usage
        metadata
        createdAt
      }
      createdAt
      updatedAt
    }
  }
`;

// Legacy alias for backwards compatibility
export const CHAT_CONVERSATION_QUERY = CHAT_QUERY;

export const MOCK_FILTERS = gql`
  query MockFilters {
    mockFilters {
      id
      name
      createdAt
      updatedAt
    }
  }
`;

export const MOCK_FILTER = gql`
  query MockFilter($id: ID!) {
    mockFilter(id: $id) {
      id
      name
      mocks {
        id
        uuid
        description
      }
      createdAt
      updatedAt
    }
  }
`;

export const ADMIN_CHATS_QUERY = gql`
  query AdminChats(
    $limit: Int
    $offset: Int
    $good: Boolean
    $evaluated: Boolean
    $from: DateTime
    $to: DateTime
    $email: String
    $chatId: String
  ) {
    adminChats(
      limit: $limit
      offset: $offset
      good: $good
      evaluated: $evaluated
      from: $from
      to: $to
      email: $email
      chatId: $chatId
    ) {
      chats {
        id
        title
        type
        createdAt
        updatedAt
        good
        notes
        hasFeedback
        user {
          asfId
          firstName
          lastName
          email
          isOldAsfMember
          numImages
          numChats
          numSocialPosts
          hasSettingsInstructions
          settingsInstructions
        }
        summary {
          summaryContent
        }
      }
      total
    }
  }
`;

export const UPDATE_CHAT_EVALUATION_MUTATION = gql`
  mutation UpdateChatEvaluation($input: ChatEvaluationInput!) {
    updateChatEvaluation(input: $input) {
      id
      good
      notes
      updatedAt
    }
  }
`;

export const USER_MEMORIES_QUERY = gql`
  query UserMemories {
    userMemories {
      id
      content
      contextType
      contextId
      metadata
      createdAt
      updatedAt
    }
  }
`;

/**
 * Variable sets for every dock read of MESSAGES_QUERY.
 *
 * The dock is DM-only. The public contact form is disabled by decision (superseded
 * by DMs — see #3672), and its historical CONTACT_FORM threads would otherwise list
 * here as DM-looking rows with no composer — which they did, because this app used
 * to pass no channel at all while town-square filtered to IN_APP.
 *
 * Apollo matches refetchQueries entries to active queries by exact variables, so
 * every read AND every refetch of the dock lists must use these same objects; do not
 * inline the variables.
 *
 * Twin of town-square/src/lib/graphql/message-queries.ts — edit both.
 */
export const DOCK_PREVIEW_MESSAGES_VARIABLES = { channel: 'IN_APP', limit: 20, offset: 0 } as const;
export const DOCK_LIST_MESSAGES_VARIABLES = { channel: 'IN_APP', limit: 50, offset: 0 } as const;

export const MESSAGES_QUERY = gql`
  ${USER_BADGE_FIELDS}

  query Messages($channel: MessageChannel, $search: String, $limit: Int, $offset: Int) {
    messages(channel: $channel, search: $search, limit: $limit, offset: $offset) {
      items {
        id
        channel
        subject
        guild {
          id
          slug
          name
        }
        participants {
          id
          userId
          contactId
          lastReadAt
          mutedAt
          blockedAt
          user {
            asfId
            username
            firstName
            lastName
            profile {
              id
              slug
              avatarUrl
              displayName
              isHumanVerified
              isDisabled
              badges {
                ...UserBadgeFields
              }
            }
          }
          contact {
            id
            name
            email
          }
        }
        lastMessagePart {
          id
          content
          sourceLocale
          translations {
            locale
            content
            status
          }
          createdAt
          senderUserId
          status
          attachments {
            id
          }
          sender {
            kind
            userId
            contactId
            user {
              asfId
              firstName
              lastName
            }
            contact {
              id
              name
            }
          }
        }
        unreadCount
        lastMessageAt
        updatedAt
        createdAt
      }
      total
    }
  }
`;

export const MESSAGE_QUERY = gql`
  ${USER_BADGE_FIELDS}

  query Message($id: ID!) {
    message(id: $id) {
      id
      channel
      subject
      guild {
        id
        slug
        name
      }
      participants {
        id
        userId
        contactId
        lastReadAt
        mutedAt
        blockedAt
        user {
          asfId
          firstName
          lastName
          profile {
            id
            slug
            avatarUrl
            displayName
            isHumanVerified
            isDisabled
            badges {
              ...UserBadgeFields
            }
          }
        }
        contact {
          id
          name
          email
        }
      }
      parts(limit: 50, offset: 0, fromEnd: true) {
        items {
          id
          content
          sourceLocale
          translations {
            locale
            content
            status
          }
          createdAt
          senderUserId
          status
          attachments {
            id
            position
            image {
              id
              optimizedUrl
              thumbnailUrl
              originalUrl
              originalWidth
              originalHeight
            }
            file {
              url
              name
              mediaType
              size
            }
          }
          sender {
            kind
            userId
            contactId
            user {
              asfId
              firstName
              lastName
              profile {
                id
                avatarUrl
                displayName
                isHumanVerified
                isDisabled
              }
            }
            contact {
              id
              name
            }
          }
        }
        total
      }
      lastMessagePart {
        id
        content
        createdAt
        senderUserId
        status
        attachments {
          id
        }
        sender {
          kind
          userId
          contactId
          user {
            asfId
            firstName
            lastName
          }
          contact {
            id
            name
          }
        }
      }
      unreadCount
      updatedAt
      createdAt
    }
  }
`;

export const ADMIN_PROFILES_QUERY = gql`
  query AdminProfiles($limit: Int, $offset: Int, $search: String, $isDiscoverable: Boolean, $isDisabled: Boolean) {
    adminProfiles(
      limit: $limit
      offset: $offset
      search: $search
      isDiscoverable: $isDiscoverable
      isDisabled: $isDisabled
    ) {
      profiles {
        id
        userId
        avatarUrl
        bio
        displayName
        artistStatement
        countryCode
        country
        regionCode
        region
        city
        latitude
        longitude
        timezone
        bannerUrl
        slug
        socialLinks
        isPublic
        isDiscoverable
        isDisabled
        isBanned
        isNsfw
        excludeFromSuggestions
        categories {
          id
          name
          slug
        }
        createdAt
        updatedAt
        user {
          asfId
          firstName
          lastName
          email
          username
          createdAt
          updatedAt
        }
        images(limit: 6) {
          id
          slug
          title
          description
          thumbnailUrl
          optimizedUrl
          createdAt
        }
      }
      total
    }
  }
`;

export const ADMIN_PROFILE_QUERY = gql`
  query AdminProfile($profileId: ID!) {
    adminProfile(profileId: $profileId) {
      id
      userId
      avatarUrl
      bio
      displayName
      artistStatement
      countryCode
      country
      regionCode
      region
      city
      latitude
      longitude
      timezone
      bannerUrl
      slug
      socialLinks
      isPublic
      isDiscoverable
      isDisabled
      watermarkEnabled
      watermarkUrl
      showMockups
      showArtworkSpecs
      showActivity
      isHumanVerified
      categories {
        id
        name
        slug
      }
      createdAt
      updatedAt
      user {
        asfId
        firstName
        lastName
        email
        username
        brandVoice
        story
        createdAt
        updatedAt
        isOldAsfMember
        isCoreFunnelLead
      }
      images {
        id
        slug
        title
        description
        thumbnailUrl
        optimizedUrl
        originalUrl
        originalWidth
        originalHeight
        height
        width
        dimensionType
        tags {
          id
          name
        }
        createdAt
      }
    }
  }
`;

export const SEARCH_COUNTRIES = gql`
  query SearchCountries($query: String!) {
    searchCountries(query: $query) {
      isoCode
      name
      phonecode
      flag
      currency
      latitude
      longitude
      timezones {
        zoneName
        gmtOffset
        gmtOffsetName
        abbreviation
        tzName
      }
    }
  }
`;

export const SEARCH_STATES = gql`
  query SearchStates($countryCode: String!, $query: String!) {
    searchStates(countryCode: $countryCode, query: $query) {
      name
      isoCode
      countryCode
      latitude
      longitude
    }
  }
`;

export const SEARCH_CITIES = gql`
  query SearchCities($countryCode: String!, $stateCode: String!, $query: String!) {
    searchCities(countryCode: $countryCode, stateCode: $stateCode, query: $query) {
      name
      countryCode
      stateCode
      latitude
      longitude
    }
  }
`;

export const GET_ALL_COUNTRIES = gql`
  query GetAllCountries {
    countries {
      isoCode
      name
      phonecode
      flag
      currency
      latitude
      longitude
      timezones {
        zoneName
        gmtOffset
        gmtOffsetName
        abbreviation
        tzName
      }
    }
  }
`;

export const GET_STATES = gql`
  query GetStates($countryCode: String!) {
    states(countryCode: $countryCode) {
      name
      isoCode
      countryCode
      latitude
      longitude
    }
  }
`;

export const GET_CITIES = gql`
  query GetCities($countryCode: String!, $stateCode: String!) {
    cities(countryCode: $countryCode, stateCode: $stateCode) {
      name
      countryCode
      stateCode
      latitude
      longitude
    }
  }
`;

export const CHECK_SLUG_AVAILABILITY = gql`
  query CheckSlugAvailability($slug: String!, $excludeUserId: ID) {
    checkSlugAvailability(slug: $slug, excludeUserId: $excludeUserId)
  }
`;

export const CHECK_IMAGE_SLUG_AVAILABILITY = gql`
  query CheckImageSlugAvailability($slug: String!, $excludeImageId: ID) {
    checkImageSlugAvailability(slug: $slug, excludeImageId: $excludeImageId)
  }
`;

export const GET_CATEGORIES = gql`
  query GetCategories {
    categories {
      id
      name
      slug
      facetId
      parentId
      isActive
      facet {
        id
        code
        name
      }
      parent {
        id
        name
        slug
        facetId
        facet {
          id
          code
          name
        }
      }
      children {
        id
        name
        slug
        facetId
        facet {
          id
          code
          name
        }
      }
    }
  }
`;

export const CONTACTS_QUERY = gql`
  query Contacts($limit: Int, $offset: Int) {
    contacts(limit: $limit, offset: $offset) {
      contacts {
        id
        email
        name
        message
        subject
        messages {
          id
          subject
          channel
          createdAt
          parts(limit: 50) {
            items {
              id
              content
              createdAt
            }
            total
          }
        }
        createdAt
        updatedAt
      }
      total
    }
  }
`;

export const NOTIFICATIONS_QUERY = gql`
  ${ACTOR_FIELDS}

  query Notifications($limit: Int, $offset: Int, $isRead: Boolean) {
    notifications(limit: $limit, offset: $offset, isRead: $isRead) {
      notifications {
        id
        type
        targetType
        targetId
        isRead
        metadata
        pointsAwarded
        sharedGuild {
          id
          name
          slug
        }
        createdAt
        updatedAt
        user {
          asfId
          firstName
          lastName
          username
          profile {
            id
            slug
          }
        }
        actor {
          ...ActorFields
        }
      }
      total
      unreadCount
    }
  }
`;

export const NOTIFICATION_COUNTS_QUERY = gql`
  query NotificationCounts {
    notifications(limit: 1, offset: 0) {
      total
      unreadCount
    }
  }
`;

export const NOTIFICATION_ADDED_SUBSCRIPTION = gql`
  ${ACTOR_FIELDS}

  subscription NotificationAdded {
    notificationAdded {
      notification {
        id
        type
        targetType
        targetId
        isRead
        metadata
        pointsAwarded
        createdAt
        updatedAt
        user {
          asfId
          firstName
          lastName
          username
          profile {
            id
            slug
          }
        }
        actor {
          ...ActorFields
        }
      }
      unreadCount
    }
  }
`;

// The three newest incoming requests ride this already-polled badge query so the
// pending-requests banner costs no extra request. `requestedBy` selects exactly the
// fields `ConnectionFields` does: `PublicUser` has no cache key, so it is stored embedded
// under `Connection:<id>`, and a narrower shape here would evict fields that query reads.
export const SOCIAL_BADGE_COUNTS_QUERY = gql`
  query SocialBadgeCounts {
    incomingConnectionRequests(first: 3) {
      totalCount
      edges {
        node {
          id
          createdAt
          requestedBy {
            asfId
            displayName
            slug
            avatarUrl
          }
        }
      }
    }
    unreadDmCount
  }
`;

// GraphQL subscriptions — not Stripe billing. Selection on messagePart matches
// MESSAGE_QUERY parts.items so cache.updateQuery can append without missing fields.
export const MESSAGE_UPDATED_SUBSCRIPTION = gql`
  subscription MessageUpdated {
    messageUpdated {
      messageId
      partId
      reactions {
        emoji
        count
        hasViewerReacted
      }
    }
  }
`;

export const MESSAGE_ADDED_SUBSCRIPTION = gql`
  subscription MessageAdded {
    messageAdded {
      messageId
      unreadDmCount
      isAuthoredByViewer
      messagePart {
        id
        content
        createdAt
        senderUserId
        status
        sender {
          kind
          userId
          contactId
          user {
            asfId
            firstName
            lastName
            profile {
              id
              avatarUrl
              displayName
              isHumanVerified
              isDisabled
            }
          }
          contact {
            id
            name
          }
        }
      }
    }
  }
`;

export const COMMUNITY_POST_ADDED_SUBSCRIPTION = gql`
  subscription CommunityPostAdded {
    communityPostAdded {
      postId
      communityId
      communitySlug
      authorUserId
      isAuthoredByViewer
    }
  }
`;

export const COMMUNITY_POSTS_SINCE_LAST_VISIT_QUERY = gql`
  query CommunityPostsSinceLastVisit {
    newCommunityPostsSinceLastVisit
  }
`;

// GTM-only body attributes, polled once a minute per open tab.
// `numChatMessages` is deliberately NOT requested: the User resolver field runs an
// uncached, no-LIMIT `ChatPart.count` over the 16 GB `chat_parts` table (~0.3-0.5 s per
// call, ~235 calls/min in prod), and the value only ever fed `data-chat-messages-sent`,
// which no tag reads. See issue #3786 — do not re-add it to a polling query.
export const USER_METRICS_QUERY = gql`
  query UserMetrics($asfId: ID!) {
    user(asfId: $asfId) {
      asfId
      createdAt
      numSocialPosts
      numSparksRan
    }
  }
`;

export const BOOKMARKED_COMMUNITY_POSTS_QUERY = gql`
  ${POST_AUTHOR_FIELDS}

  query BookmarkedCommunityPosts($first: Int, $after: String, $targetType: BookmarkTargetType) {
    bookmarkedItems(first: $first, after: $after, targetType: $targetType) {
      edges {
        node {
          id
          targetType
          targetId
          createdAt
          communityPost {
            id
            title
            content
            slug
            community {
              slug
              name
            }
            author {
              ...PostAuthorFields
            }
            socialImages {
              id
              thumbnailUrl
            }
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

const CLIENT_PLATFORM_FEED_CARD_NODE_FIELDS = gql`
  fragment ClientPlatformFeedCardNodeFields on PlatformFeedCard {
    id
    imageUrl
    destinationUrl
    altText
    commentsEnabled
    commentCount
  }
`;

const CLIENT_COMMUNITY_FEED_ITEM_FIELDS = gql`
  ${CLIENT_PLATFORM_FEED_CARD_NODE_FIELDS}
  ${POST_AUTHOR_FIELDS}
  fragment ClientCommunityFeedItemFields on CommunityFeedItem {
    __typename
    ... on CommunityPost {
      id
      slug
      title
      type
      content
      status
      voteScore
      commentCount
      createdAt
      author {
        ...PostAuthorFields
      }
      community {
        id
        name
        slug
      }
    }
    ... on PlatformFeedCard {
      ...ClientPlatformFeedCardNodeFields
    }
  }
`;

const CLIENT_COMMUNITY_FEED_ITEM_CHAT_FIELDS = gql`
  ${CLIENT_PLATFORM_FEED_CARD_NODE_FIELDS}
  fragment ClientCommunityFeedItemChatFields on CommunityFeedItem {
    __typename
    ... on CommunityPost {
      id
      slug
      title
      content
      commentCount
      voteScore
      community {
        slug
        name
      }
      author {
        asfId
        firstName
        lastName
        profile {
          id
          displayName
          avatarUrl
        }
      }
    }
    ... on PlatformFeedCard {
      ...ClientPlatformFeedCardNodeFields
    }
  }
`;

export const GET_TRENDING_COMMUNITY_POSTS_FOR_CHAT_QUERY = gql`
  ${CLIENT_COMMUNITY_FEED_ITEM_CHAT_FIELDS}
  query GetTrendingCommunityPostsForChat($first: Int!) {
    trendingCommunityPosts(first: $first) {
      totalCount
      edges {
        node {
          ...ClientCommunityFeedItemChatFields
        }
      }
    }
  }
`;

/** One member's profile hover card; same selection as town-square's PROFILE_CARD_QUERY. */
export const PROFILE_CARD_QUERY = gql`
  query ProfileCard($slug: String!) {
    profileCard(slug: $slug) {
      slug
      connectionCount
      isFollowedByViewer
      user {
        asfId
        firstName
        lastName
        displayName
        slug
        avatarUrl
        bio
        city
        region
        followerCount
        isHumanVerified
        artworkThumbs
      }
      connectionState {
        userId
        connectionId
        status
        requestedByViewer
        isBlockedByViewer
      }
    }
  }
`;

export const IS_FOLLOWING_BULK_QUERY = gql`
  query IsFollowingBulk($userIds: [ID!]!) {
    isFollowingBulk(userIds: $userIds)
  }
`;

// Lightweight membership IDs for hydrating the Join Communities step on Back-nav.
export const MY_COMMUNITY_IDS_QUERY = gql`
  query MyCommunityIds {
    myCommunities(limit: 500) {
      id
    }
  }
`;

// Joined communities for the sidebar "My Communities" group. Ordered by activity
// server-side (myCommunities sorts lastActivityAt DESC), so the most-active
// communities surface at the top without a client sort. Community pages live in
// the town-square app, so the sidebar rows link out to ${TOWN_SQUARE_URL}/community/<slug>.
export const MY_COMMUNITIES_QUERY = gql`
  query MyCommunities {
    user {
      asfId
    }
    myCommunities(limit: 200) {
      id
      name
      slug
      avatarUrl
      hasUnseenPosts
    }
  }
`;

// The viewer's guilds for the sidebar "My Guilds" group. Guild pages live in the
// town-square app, so the rows link out to ${TOWN_SQUARE_URL}/guilds/<slug>.
export const MY_GUILDS_QUERY = gql`
  query MyGuilds {
    user {
      asfId
    }
    myGuilds {
      id
      name
      slug
      viewerUnreadChat
    }
  }
`;

export const ME_QUERY = gql`
  query Me {
    user {
      asfId
      firstName
      lastName
      createdAt
      welcomeCompletedAt
      hasCommunityActivity
      onboardingPrefill
      profile {
        id
        avatarUrl
        bio
        displayName
        socialLinks
        onboardingIntent
        careerStage
        practiceFrequency
        categories {
          id
          name
          slug
        }
      }
    }
  }
`;

/** Welcome completion screen: live counts (root fields avoid nested User resolvers / cache quirks) */
export const WELCOME_DONE_STATS_QUERY = gql`
  query WelcomeDoneStats {
    profile {
      followingCount
      slug
    }
    imageStats {
      count
    }
    myCommunities(limit: 500) {
      id
    }
  }
`;

export const MY_ONBOARDING_SESSION = gql`
  query MyOnboardingSession {
    myOnboardingSession {
      id
      currentStepCode
      completedRequiredCount
      completedTotalCount
      outcome
      startedAt
      completedAt
      lastActiveAt
      flowVersion {
        id
        version
        name
        isDefault
        steps {
          id
          stepCode
          position
          isEnabled
          configOverrides
          config
          isRequired
          description
          rationale
        }
      }
    }
  }
`;

export const MY_ONBOARDING_IMAGES_READINESS = gql`
  query MyOnboardingImagesReadiness {
    myOnboardingImagesReadiness {
      imageId
      optimizedAt
      visualEmbeddingAt
      aiMetadataAt
      optimizedUrl
      thumbnailUrl
    }
  }
`;

export const SUGGESTED_ARTISTS_FOR_ONBOARDING = gql`
  query SuggestedArtistsForOnboarding {
    recommendedArtists(limit: 36) {
      reason
      similarityScore
      user {
        asfId
        username
        firstName
        lastName
        profile {
          id
          avatarUrl
          bio
          displayName
          isHumanVerified
        }
      }
    }
  }
`;

export const SUGGESTED_COMMUNITIES_FOR_ONBOARDING = gql`
  query SuggestedCommunitiesForOnboarding {
    recommendedCommunities(limit: 6) {
      reason
      memberCount
      community {
        id
        name
        description
        slug
        avatarUrl
        bannerUrl
        postCount
        memberCount
      }
    }
  }
`;

export const GET_TRENDING_COMMUNITY_POSTS = gql`
  ${CLIENT_COMMUNITY_FEED_ITEM_FIELDS}
  query GetTrendingCommunityPosts($first: Int, $after: String) {
    trendingCommunityPosts(first: $first, after: $after) {
      edges {
        node {
          ...ClientCommunityFeedItemFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_COMMUNITY_BY_SLUG = gql`
  query GetCommunityBySlug($slug: String!) {
    community(slug: $slug) {
      id
      slug
      name
    }
  }
`;

export const GET_ONBOARDING_VERSIONS = gql`
  query GetOnboardingVersions {
    onboardingVersions {
      id
      version
      name
      description
      isDefault
      totalStepCount
      requiredStepCount
      createdAt
    }
  }
`;

export const GET_ONBOARDING_FUNNEL = gql`
  query GetOnboardingFunnel($flowVersionId: ID!, $filters: OnboardingCohortFilters) {
    onboardingFunnel(flowVersionId: $flowVersionId, filters: $filters) {
      flowVersionId
      totalSessions
      completedSessions
      abandonedSessions
      inProgressSessions
      completionRate
      steps {
        stepCode
        position
        viewedCount
        submittedCount
        skippedCount
        errorCount
        fallbackRate
        avgDurationMs
        dropOffRate
      }
    }
  }
`;

export const GET_USER_ONBOARDING_SESSION = gql`
  query GetUserOnboardingSession($userId: ID!) {
    onboardingSession(userId: $userId) {
      id
      startedAt
      completedAt
      outcome
      user {
        asfId
        email
        username
        firstName
        lastName
      }
      flowVersion {
        id
        version
        name
      }
      events {
        id
        stepCode
        eventType
        durationMs
        payload
        createdAt
      }
    }
  }
`;

export const SEARCH_ONBOARDING_USER_SESSIONS = gql`
  query SearchOnboardingUserSessions($query: String!, $limit: Int) {
    onboardingUserSessions(query: $query, limit: $limit) {
      id
      startedAt
      completedAt
      outcome
      user {
        asfId
        email
        username
        firstName
        lastName
      }
      flowVersion {
        id
        version
        name
      }
    }
  }
`;

export const GET_AVAILABLE_STEP_CODES = gql`
  query GetAvailableStepCodes {
    availableStepCodes
  }
`;

export const GET_ONBOARDING_STEP_REGISTRY = gql`
  query GetOnboardingStepRegistry {
    onboardingStepRegistry {
      code
      isRequired
      isPostCompletion
      description
      rationale
      defaultConfig
      configSchema {
        key
        type
        label
        description
        default
        min
        max
        maxLength
        choices
      }
    }
  }
`;

export const CONTENT_REVIEW_REQUESTS_QUERY = gql`
  query ContentReviewRequests($limit: Int, $offset: Int) {
    contentReviewRequests(limit: $limit, offset: $offset) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      edges {
        node {
          id
          userId
          message
          createdAt
          user {
            asfId
            email
            firstName
            lastName
          }
          profile {
            slug
            displayName
          }
        }
      }
    }
  }
`;

export const FLAGGED_IMAGES_QUERY = gql`
  query FlaggedImages($userId: ID!, $scanType: String, $limit: Int, $offset: Int) {
    flaggedImages(userId: $userId, scanType: $scanType, limit: $limit, offset: $offset) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      edges {
        node {
          id
          contentType
          contentId
          scanType
          isFlagged
          result
          scannedAt
          image {
            id
            title
            thumbnailUrl
            optimizedUrl
          }
          socialImage {
            id
            thumbnailUrl
            optimizedUrl
          }
          communityPost {
            id
            slug
            title
            type
            content
            socialImages {
              id
              thumbnailUrl
              optimizedUrl
            }
            textImages {
              id
              thumbnailUrl
              optimizedUrl
            }
          }
          communityComment {
            id
            content
            post {
              id
              title
              slug
            }
          }
          community {
            slug
          }
        }
      }
    }
  }
`;

/**
 * Deliberately narrow: no step configs and no per-wizard counts, so listing wizards never
 * loads authored copy it will not show and never runs an aggregate. `steps { id }` is the
 * step count and nothing more; the per-row preview fetches the configs lazily on click.
 */
export const GET_FEATURE_WIZARDS = gql`
  query GetFeatureWizards {
    featureWizards {
      id
      slug
      name
      status
      controlPercent
      steps {
        id
      }
    }
  }
`;

export const GET_FEATURE_WIZARD = gql`
  query GetFeatureWizard($id: ID!) {
    featureWizard(id: $id) {
      id
      slug
      name
      description
      status
      publishedAt
      controlPercent
      targetEventName
      handoffHref
      steps {
        id
        stepCode
        position
        config
      }
    }
  }
`;

export const GET_FEATURE_WIZARD_STEP_REGISTRY = gql`
  query GetFeatureWizardStepRegistry {
    featureWizardStepRegistry {
      code
      isRequired
      description
      rationale
      defaultConfig
      configSchema {
        key
        type
        label
        description
        default
        min
        max
        maxLength
        choices
      }
    }
  }
`;

/** Both audiences read the same shape; only the control audience's values are nulled. */
const FEATURE_WIZARD_AUDIENCE_FIELDS = gql`
  fragment FeatureWizardAudienceFields on FeatureWizardAudienceFunnel {
    assignment
    arrivedCount
    openedCount
    completedCount
    dismissedCount
    openRate
    completionRate
    dismissRate
    terminalRate
  }
`;

/**
 * One wizard's secondary in-product funnel. Deliberately NOT folded into
 * `GET_FEATURE_WIZARDS`: this runs aggregates, and listing wizards must never run one.
 */
export const GET_FEATURE_WIZARD_FUNNEL = gql`
  query GetFeatureWizardFunnel($wizardId: ID!) {
    featureWizardFunnel(wizardId: $wizardId) {
      wizardId
      slug
      controlPercent
      targetEventName
      audienceProperty
      totalReach
      wizardAudience {
        ...FeatureWizardAudienceFields
      }
      controlAudience {
        ...FeatureWizardAudienceFields
      }
      steps {
        stepPosition
        stepCode
        config
        viewedCount
        submittedCount
      }
    }
  }
  ${FEATURE_WIZARD_AUDIENCE_FIELDS}
`;

/**
 * The overlay host's one read. Side-effect free — the ARRIVED row is
 * `recordFeatureWizardEvent`'s job — so it is safe on every page load that carries a wizard
 * parameter. Returns null for an unknown, unpublished or already-finished wizard, and a
 * control-audience viewer gets an empty `steps` list because control never receives the copy.
 */
export const MY_FEATURE_WIZARD = gql`
  query MyFeatureWizard($slug: String!) {
    myFeatureWizard(slug: $slug) {
      id
      slug
      name
      handoffHref
      assignment
      steps {
        position
        stepCode
        config
      }
    }
  }
`;

/**
 * Every saved satellite item for the viewer, unpaginated. The row carries its own
 * display copy, so a card renders with no follow-up request to the satellite that
 * produced it — the tabs stay whole even when that app is down.
 */
export const USER_EXTERNAL_SAVES_QUERY = gql`
  query UserExternalSaves {
    userExternalSaves {
      id
      app
      externalId
      display {
        title
        thumbnailUrl
        subtitle
      }
      createdAt
    }
  }
`;

/**
 * Whether this artwork accepts comments, and the few most recent ones.
 *
 * `isEnabled` is the artist's own switch (`profiles.isArtworkCommentsEnabled`) — a
 * composer must never be offered on a piece whose owner turned comments off.
 */
export const IMAGE_COMMENTS_QUERY = gql`
  query ImageComments($imageId: ID!, $first: Int) {
    imageComments(imageId: $imageId, first: $first) {
      isEnabled
      viewerIsOwner
      comments {
        edges {
          node {
            id
            content
            createdAt
            profile {
              slug
              displayName
              avatarUrl
            }
          }
        }
      }
    }
  }
`;

/**
 * One community post, enough of it to read and answer without leaving the page.
 *
 * Deliberately narrower than town-square's feed fragment: no contest, poll, critique or
 * translation fields. A notification opens this to be read and replied to — the full
 * post, with everything its type can carry, is what the "Open in community" link is for.
 */
export const COMMUNITY_POST_BY_SLUG_QUERY = gql`
  ${POST_AUTHOR_FIELDS}

  query CommunityPostBySlug($communitySlug: String!, $postSlug: String!, $targetCommentId: ID) {
    communityPostBySlug(communitySlug: $communitySlug, postSlug: $postSlug) {
      id
      title
      slug
      type
      content
      voteScore
      commentCount
      createdAt
      isEdited
      sourceLocale
      translations {
        locale
        title
        content
        status
      }
      currentUserVote {
        id
        direction
      }
      community {
        slug
        name
      }
      author {
        ...PostAuthorFields
      }
      socialImages {
        id
        optimizedUrl
        thumbnailUrl
        originalWidth
        originalHeight
        isNsfw
      }
      textImages {
        id
        optimizedUrl
        thumbnailUrl
        originalWidth
        originalHeight
        isNsfw
      }
      mediaItems {
        kind
        socialImageId
        youtubeVideoId
        videoId
        video {
          id
          status
          url
          thumbnailUrl
          width
          height
        }
      }
      poll {
        id
        endsAt
        totalVoteCount
        currentUserOptionId
        options {
          id
          label
          position
          voteCount
          socialImage {
            id
            optimizedUrl
            thumbnailUrl
            isNsfw
          }
        }
      }
      critiqueRequest {
        id
        isOpen
        closesAt
        intent
        focusAreas
        stage
        medium
        dimensions
        critiqueCount
      }
      pricingRequest {
        id
        currency
        minValueCents
        maxValueCents
        isOpen
        currentUserValueCents
        canSeeResults
        results {
          submissionCount
          averageCents
          medianCents
          lowCents
          highCents
        }
      }
      ratingRequest {
        id
        isOpen
        currentUserRating
        canSeeResults
        results {
          submissionCount
          average
          median
        }
      }
      commentThreads(limit: 20, targetCommentId: $targetCommentId) {
        id
        content
        createdAt
        voteScore
        sourceLocale
        translations {
          locale
          content
          status
        }
        author {
          ...PostAuthorFields
        }
        critique {
          id
        }
      }
    }
  }
`;

export const REACTIONS_QUERY = gql`
  query Reactions($targetType: ReactionTargetType!, $targetId: ID!) {
    reactions(targetType: $targetType, targetId: $targetId) {
      emoji
      count
      hasViewerReacted
    }
  }
`;

export const COMMUNITIES_FOR_COMPOSE_QUERY = gql`
  query CommunitiesForCompose($limit: Int) {
    myCommunities(limit: $limit) {
      id
      slug
      name
    }
  }
`;

/**
 * The Human-Made Art badge popover's certificate (`HumanMadeBadgePopover`):
 * the public read town-square's `/verified/[slug]` uses, cut to what the card
 * prints. Fetched only when the card opens. Twin of town-square's
 * `HUMAN_MADE_CERTIFICATE` in `lib/graphql/verification-queries.ts`.
 */
export const HUMAN_MADE_CERTIFICATE = gql`
  query HumanMadeCertificate($slug: String!) {
    verifiedArtistBySlug(slug: $slug) {
      id
      slug
      displayName
      firstName
      lastName
      verificationNumber
      dateVerified
    }
  }
`;
